import React, { useEffect, useRef, useState } from "react";
import { http, fileUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Upload, Link2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

// value: array of {type: 'upload'|'url', value: string}
const ImagesInput = ({ value = [], onChange, testIdPrefix = "hotel" }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  const addImage = (img) => onChange([...(value || []), img]);
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await http.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      addImage({ type: "upload", value: res.data.storage_path });
      toast.success("Imagen subida");
    } catch (err) {
      toast.error("No se pudo subir la imagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleAddUrl = () => {
    const v = urlInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) { toast.error("Debe iniciar con http:// o https://"); return; }
    addImage({ type: "url", value: v });
    setUrlInput("");
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="rounded-xl"
        >
          {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
          Subir archivo
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <Link2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="https://..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddUrl())}
              className="pl-9 rounded-xl bg-white"
              data-testid={`${testIdPrefix}-url-input`}
            />
          </div>
          <Button type="button" onClick={handleAddUrl} variant="secondary" className="rounded-xl" data-testid={`${testIdPrefix}-url-add-btn`}>
            Agregar URL
          </Button>
        </div>
      </div>

      {(value || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((img, i) => (
            <div key={i} className="relative group aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={img.type === "url" ? img.value : fileUrl(img.value)}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.style.opacity = 0.3)}
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testIdPrefix}-image-remove-${i}`}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImagesInput;
